import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { locations, formatAddress } from '../config/locations';
import { theme } from '../config/theme';

const LocationContainer = styled(motion.section)`
  padding: ${theme.spacing.sectionPadding.vertical} ${theme.spacing.sectionPadding.horizontal};
  background-color: ${theme.colors.primary.background};
  position: relative;
  overflow: hidden;
`;

const LocationContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
`;

const LocationInfo = styled(motion.div)`
  padding: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
    text-align: center;
  }
`;


const StudioSelector = styled(motion.div)`
  margin-top: ${theme.spacing.xl};
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.md};
  }
`;

const StudioOption = styled.button`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.$selected ? theme.colors.accent.main : theme.colors.primary.text};
  background: none;
  border: none;
  cursor: pointer;
  transition: ${theme.transitions.default};
  padding: ${theme.spacing.sm} 0;
  position: relative;
  opacity: ${props => props.$selected ? 1 : 0.7};
  
  &:hover {
    color: ${theme.colors.accent.main};
    opacity: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.$selected ? '100%' : '0'};
    height: 1px;
    background-color: ${theme.colors.accent.main};
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  &:not(:last-child):after {
    content: '/';
    position: absolute;
    right: -${theme.spacing.md};
    top: 50%;
    transform: translateY(-50%);
    width: auto;
    height: auto;
    background: none;
    color: ${theme.colors.primary.text};
    opacity: 0.3;
  }
`;

const HoursContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;


const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.xs} 0;
  border-bottom: 1px solid ${theme.shadows.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const DayName = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${theme.colors.primary.text};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 80px;
`;

const HoursTime = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${theme.colors.primary.text};
  opacity: 0.8;
`;

const MapContainer = styled(motion.div)`
  position: relative;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px ${theme.shadows.medium};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 300px;
  }
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  filter: grayscale(20%) sepia(10%) hue-rotate(15deg) saturate(1.1);
  transition: filter 0.3s ease;
  
  &:hover {
    filter: grayscale(0%) sepia(0%) hue-rotate(0deg) saturate(1);
  }
`;

const MapOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.1) 0%,
    transparent 50%,
    rgba(0, 0, 0, 0.05) 100%
  );
  pointer-events: none;
`;

const Location = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [selectedLocationId, setSelectedLocationId] = useState(locations[0].id);
  const selectedLocation = locations.find(loc => loc.id === selectedLocationId) || locations[0];

  const containerVariants = {
    hidden: { opacity: 0, y: theme.animations.moveMedium },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.medium,
        ease: 'easeOut',
        staggerChildren: theme.animations.staggerMedium,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: theme.animations.moveSmall },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.fast,
        ease: 'easeOut',
      },
    },
  };

  const mapVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: theme.animations.medium,
        ease: 'easeOut',
        delay: theme.animations.staggerMedium,
      },
    },
  };

  const formatDayName = (day) => {
    const days = {
      monday: 'MON',
      tuesday: 'TUE', 
      wednesday: 'WED',
      thursday: 'THU',
      friday: 'FRI',
      saturday: 'SAT',
      sunday: 'SUN'
    };
    return days[day] || day.toUpperCase();
  };

  return (
    <LocationContainer
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={containerVariants}
    >
      <LocationContent>
        <LocationInfo>
          <HoursContainer variants={itemVariants}>
            {Object.entries(selectedLocation.hours).map(([day, hours]) => (
              <HoursRow key={day}>
                <DayName>{formatDayName(day)}</DayName>
                <HoursTime>{hours}</HoursTime>
              </HoursRow>
            ))}
          </HoursContainer>
        </LocationInfo>
        
        <MapContainer variants={mapVariants}>
          <MapFrame
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyB1JJhJZwpkf6VZus6Tu5I5OOcuYxofy1I&q=${encodeURIComponent(formatAddress(selectedLocation.address))}&zoom=15`}
            title={`Map of ${selectedLocation.name}`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <MapOverlay />
        </MapContainer>
      </LocationContent>
      
      <StudioSelector variants={itemVariants}>
        {locations.map((location) => (
          <StudioOption
            key={location.id}
            $selected={selectedLocationId === location.id}
            onClick={() => setSelectedLocationId(location.id)}
          >
            {location.displayName}
          </StudioOption>
        ))}
      </StudioSelector>
    </LocationContainer>
  );
};

export default Location;